import { useEffect, useState } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { useAuth } from "../Context/AuthContext";
import { FaTrash, FaTrashRestore, FaEye, FaPlus } from "react-icons/fa";
import "./HolidayMasterOrg.css"; // Import the new CSS file

import {
  getHolidaysByOrgId,
  getAllHolidaysByOrgId,
  getDeletedHolidaysByOrgId,
  createHoliday,
  updateHoliday,
  softDeleteHoliday,
  restoreHoliday,
  hardDeleteHoliday,
} from "../Api/HolidayMasterApi";

const HolidayMasterOrg = () => {
  const { user } = useAuth();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingHolidayId, setEditingHolidayId] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("active");
  const ALLOWED_ROLES_FOR_ADD = ["HR", "Admin"];
  const [searchName, setSearchName] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [filterType, setFilterType] = useState("");

  const canAddHoliday = user?.roles?.some((role) =>
    ALLOWED_ROLES_FOR_ADD.includes(role),
  );

  const [formData, setFormData] = useState({
    holidayName: "",
    holidayDate: "",
    yearId: "",
    monthId: "",
    holidayType: "",
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null);

  const loadHolidays = async () => {
    try {
      let data;
      if (viewMode === "active") {
        data = await getHolidaysByOrgId(user?.organisationId);
      } else if (viewMode === "deleted") {
        data = await getDeletedHolidaysByOrgId(user?.organisationId);
      } else {
        data = await getAllHolidaysByOrgId(user?.organisationId);
      }
      setHolidays(data || []);
    } catch (error) {
      toast.error("Error fetching holidays");
    }
  };

  const MONTH_MAP = {
    "01": "January",
    "02": "February",
    "03": "March",
    "04": "April",
    "05": "May",
    "06": "June",
    "07": "July",
    "08": "August",
    "09": "September",
    10: "October",
    11: "November",
    12: "December",
  };

  const HOLIDAY_TYPE_OPTIONS = [
    { value: "", label: "Select Holiday Type" },
    { value: "National", label: "National" },
    { value: "Festival", label: "Festival" },
    { value: "Optional", label: "Optional" },
    { value: "Company", label: "Company" },
    { value: "Weekend", label: "Weekend" },
    { value: "Restricted", label: "Restricted" },
  ];

  useEffect(() => {
    if (user?.organisationId) {
      loadHolidays();
    }
  }, [user?.organisationId, viewMode]);

  const handleSaveHoliday = async () => {
    if (!formData.holidayName || !formData.holidayType) {
      toast.error("Holiday name and type are required");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        orgId: String(user.organisationId),
        holidayDate: formData.holidayDate || null,
        ...(isEditMode && { holidayId: editingHolidayId }),
      };

      if (isEditMode) {
        await updateHoliday(payload, String(user?.userId));
        toast.success("Holiday updated successfully");
      } else {
        await createHoliday(payload, String(user?.userId));
        toast.success("Holiday added successfully");
      }

      setShowModal(false);
      setIsEditMode(false);
      setEditingHolidayId(null);
      setFormData({
        holidayName: "",
        holidayDate: "",
        yearId: "",
        monthId: "",
        holidayType: "",
      });
      loadHolidays();
    } catch (error) {
      toast.error("Error saving holiday");
    } finally {
      setLoading(false);
    }
  };

  const handleSoftDelete = async () => {
    if (!holidayToDelete) return;
    try {
      await softDeleteHoliday(holidayToDelete.holidayId, String(user?.userId));
      toast.success("Holiday deleted successfully");
      setShowDeleteModal(false);
      setHolidayToDelete(null);
      loadHolidays();
    } catch (error) {
      toast.error("Error deleting holiday");
    }
  };

  const handleHardDelete = async () => {
    if (!holidayToDelete) return;
    try {
      await hardDeleteHoliday(holidayToDelete.holidayId);
      toast.success("Holiday permanently deleted");
      setShowDeleteModal(false);
      setHolidayToDelete(null);
      loadHolidays();
    } catch (error) {
      toast.error("Error permanently deleting holiday");
    }
  };

  const handleRestore = async (holiday) => {
    if (window.confirm(`Are you sure you want to restore "${holiday.holidayName}"?`)) {
      try {
        await restoreHoliday(holiday.holidayId, String(user?.userId));
        toast.success("Holiday restored successfully");
        loadHolidays();
      } catch (error) {
        toast.error("Error restoring holiday");
      }
    }
  };

  const handleDateChange = (dateValue) => {
    if (!dateValue) {
      setFormData({
        ...formData,
        holidayDate: "",
        monthId: "",
        yearId: "",
      });
      return;
    }

    const selectedDate = new Date(dateValue);
    const monthNumber = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const year = selectedDate.getFullYear();

    setFormData({
      ...formData,
      holidayDate: dateValue,
      monthId: monthNumber,
      yearId: String(year),
    });
  };

  const handleEditHoliday = (holiday) => {
    setIsEditMode(true);
    setEditingHolidayId(holiday.holidayId);
    setFormData({
      holidayName: holiday.holidayName,
      holidayDate: holiday.holidayDate ? moment(holiday.holidayDate).format("YYYY-MM-DD") : "",
      monthId: holiday.monthId || "",
      yearId: holiday.yearId || "",
      holidayType: holiday.holidayType,
    });
    setShowModal(true);
  };

  const filteredHolidays = holidays.filter((holiday) => {
    const matchesName = holiday.holidayName?.toLowerCase().includes(searchName.toLowerCase());
    const matchesDate = searchDate ? moment(holiday.holidayDate).format("YYYY-MM-DD") === searchDate : true;
    const matchesType = filterType ? holiday.holidayType === filterType : true;
    return matchesName && matchesDate && matchesType;
  });

  return (
    <div className="holiday-master-container">
      <Container fluid className="holiday-master-wrapper">
        {/* Header */}
        <div className="holiday-master-header d-flex justify-content-between align-items-center">
          <div className="holiday-master-title-wrapper">
            <h1 className="holiday-master-title">Holiday Master</h1>
            <p className="holiday-master-subtitle">Manage organization holidays efficiently</p>
          </div>

          <div className="holiday-master-actions">
            <div className="holiday-master-view-group">
              <button
                className={`holiday-master-view-btn ${viewMode === "active" ? "active" : ""}`}
                onClick={() => setViewMode("active")}
              >
                Active
              </button>
              <button
                className={`holiday-master-view-btn ${viewMode === "deleted" ? "active" : ""}`}
                onClick={() => setViewMode("deleted")}
              >
                Deleted
              </button>
              <button
                className={`holiday-master-view-btn ${viewMode === "all" ? "active" : ""}`}
                onClick={() => setViewMode("all")}
              >
                All
              </button>
            </div>

            {canAddHoliday && viewMode !== "deleted" && (
              <Button className="holiday-master-add-btn" onClick={() => setShowModal(true)}>
                <FaPlus /> Add Holiday
              </Button>
            )}
          </div>
        </div>

        {/* Info Alert */}
        {viewMode === "deleted" && (
          <Alert variant="info" className="holiday-master-alert">
            <FaEye className="me-2" />
            Showing soft-deleted holidays. You can restore them or permanently delete them.
          </Alert>
        )}

        {/* Filter Section */}
        <div className="holiday-master-filters">
          <Row>
            <Col md={4}>
              <Form.Control
                className="holiday-master-filter-input"
                type="text"
                placeholder="🔍 Search by Holiday Name"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Control
                className="holiday-master-filter-input"
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Select
                className="holiday-master-filter-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">All Types</option>
                {HOLIDAY_TYPE_OPTIONS.slice(1).map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-danger"
                className="holiday-master-reset-btn"
                onClick={() => {
                  setSearchName("");
                  setSearchDate("");
                  setFilterType("");
                }}
              >
                🔄 RESET
              </Button>
            </Col>
          </Row>
        </div>

        {/* Table */}
        <div className="holiday-master-table-wrapper">
          <Table bordered hover className="holiday-master-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Holiday Name</th>
                <th>Date</th>
                <th>Type</th>
                {viewMode === "all" && <th>Status</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredHolidays.length > 0 ? (
                filteredHolidays.map((holiday, index) => (
                  <tr
                    key={holiday.holidayId}
                    className={!holiday.isActive ? "holiday-master-row-deleted" : ""}
                  >
                    <td>{index + 1}</td>
                    <td><strong>{holiday.holidayName}</strong></td>
                    <td>
                      {holiday.holidayDate
                        ? moment(holiday.holidayDate).format("DD-MMM-YYYY")
                        : "-"}
                    </td>
                    <td>
                      <span className={`badge ${holiday.holidayType === 'National' ? 'bg-primary' : 
                        holiday.holidayType === 'Festival' ? 'bg-success' : 
                        holiday.holidayType === 'Optional' ? 'bg-info' : 'bg-secondary'}`}>
                        {holiday.holidayType}
                      </span>
                    </td>
                    {viewMode === "all" && (
                      <td>
                        <span className={`holiday-master-badge ${
                          holiday.isActive ? "holiday-master-badge-active" : "holiday-master-badge-deleted"
                        }`}>
                          {holiday.isActive ? "✓ Active" : "✗ Deleted"}
                        </span>
                      </td>
                    )}
                    <td className="holiday-master-actions-cell">
                      {viewMode === "deleted" ? (
                        <>
                          <button
                            className="holiday-master-action-btn holiday-master-restore-btn"
                            onClick={() => handleRestore(holiday)}
                          >
                            <FaTrashRestore /> Restore
                          </button>
                          <button
                            className="holiday-master-action-btn holiday-master-permanent-btn"
                            onClick={() => {
                              setHolidayToDelete(holiday);
                              setDeleteType("hard");
                              setShowDeleteModal(true);
                            }}
                          >
                            <FaTrash /> Permanent
                          </button>
                        </>
                      ) : (
                        <>
                          {canAddHoliday && (
                            <button
                              className="holiday-master-action-btn holiday-master-update-btn"
                              onClick={() => handleEditHoliday(holiday)}
                            >
                              ✏️ Update
                            </button>
                          )}
                          {canAddHoliday && (
                            <button
                              className="holiday-master-action-btn holiday-master-delete-btn"
                              onClick={() => {
                                setHolidayToDelete(holiday);
                                setDeleteType("soft");
                                setShowDeleteModal(true);
                              }}
                            >
                              <FaTrash /> Delete
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={viewMode === "all" ? 6 : 5} className="text-center">
                    <div className="holiday-master-empty-state">
                      <div className="holiday-master-empty-icon">📅</div>
                      <div className="holiday-master-empty-text">No holidays found</div>
                      <div className="holiday-master-empty-subtext">
                        {viewMode === "deleted" 
                          ? "No holidays in trash" 
                          : "Click 'Add Holiday' to create one"}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
          className="holiday-master-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {deleteType === "soft" ? "🗑️ Delete Holiday" : "⚠️ Permanently Delete Holiday"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {deleteType === "soft" ? (
              <>
                <p>
                  Are you sure you want to delete{" "}
                  <strong>"{holidayToDelete?.holidayName}"</strong>?
                </p>
                <div className="holiday-master-delete-warning">
                  <small>ℹ️ This holiday will be moved to trash. You can restore it later.</small>
                </div>
              </>
            ) : (
              <>
                <p>
                  Are you sure you want to{" "}
                  <strong className="text-danger">PERMANENTLY DELETE</strong>{" "}
                  <strong>"{holidayToDelete?.holidayName}"</strong>?
                </p>
                <div className="holiday-master-permanent-warning">
                  <strong>⚠️ DANGER ZONE ⚠️</strong>
                  <p className="mb-0 mt-2">This action cannot be undone! The holiday will be completely removed from the database.</p>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" className="holiday-master-modal-cancel-btn" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button
              className={deleteType === "soft" ? "holiday-master-delete-btn" : "holiday-master-permanent-btn"}
              onClick={deleteType === "soft" ? handleSoftDelete : handleHardDelete}
              style={{ border: 'none' }}
            >
              {deleteType === "soft" ? "Move to Trash" : "Permanently Delete"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Add/Edit Holiday Modal */}
        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setIsEditMode(false);
            setEditingHolidayId(null);
          }}
          centered
          className="holiday-master-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {isEditMode ? "✏️ Update Holiday" : "➕ Add Holiday"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="holiday-master-form-label">Holiday Name *</Form.Label>
                    <Form.Control
                      className="holiday-master-form-control"
                      placeholder="Enter holiday name"
                      value={formData.holidayName}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          holidayName: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="holiday-master-form-label">Holiday Date</Form.Label>
                    <Form.Control
                      className="holiday-master-form-control"
                      type="date"
                      value={formData.holidayDate}
                      onChange={(e) => handleDateChange(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="holiday-master-form-label">Holiday Type *</Form.Label>
                    <Form.Select
                      className="holiday-master-form-control"
                      value={formData.holidayType}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          holidayType: e.target.value,
                        })
                      }
                    >
                      {HOLIDAY_TYPE_OPTIONS.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="holiday-master-form-label">Month</Form.Label>
                    <Form.Control
                      className="holiday-master-form-control"
                      value={MONTH_MAP[formData.monthId] || "Auto-filled from date"}
                      disabled
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="holiday-master-form-label">Year</Form.Label>
                    <Form.Control
                      className="holiday-master-form-control"
                      value={formData.yearId || "Auto-filled from date"}
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" className="holiday-master-modal-cancel-btn" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button className="holiday-master-modal-save-btn" onClick={handleSaveHoliday} disabled={loading}>
              {loading ? "Processing..." : (isEditMode ? "Update Holiday" : "Save Holiday")}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default HolidayMasterOrg;

// import { useEffect, useState } from "react";
// import {
//   Container,
//   Table,
//   Button,
//   Modal,
//   Form,
//   Row,
//   Col,
//   Alert,
// } from "react-bootstrap";
// // import { FaPlus, FaTrash, FaRestore, FaEye } from "react-icons/fa";
// import { toast } from "react-toastify";
// import moment from "moment";
// import { useAuth } from "../Context/AuthContext";
// import "../Styling/AllTables.css";
// import { FaTrash } from "react-icons/fa";
// import { FaTrashRestore } from "react-icons/fa";
// import { FaEye } from "react-icons/fa";
// import { FaPlus } from "react-icons/fa";

// import {
//   getHolidaysByOrgId,
//   getAllHolidaysByOrgId,
//   getDeletedHolidaysByOrgId,
//   createHoliday,
//   updateHoliday,
//   softDeleteHoliday,
//   restoreHoliday,
//   hardDeleteHoliday,
// } from "../Api/HolidayMasterApi";

// const HolidayMasterOrg = () => {
//   const { user } = useAuth();
//   console.log("Logged-in user: ", user);
//   const [isEditMode, setIsEditMode] = useState(false);
//   const [editingHolidayId, setEditingHolidayId] = useState(null);

//   const [holidays, setHolidays] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const [viewMode, setViewMode] = useState("active"); // "active", "deleted", "all"

//   const ALLOWED_ROLES_FOR_ADD = ["HR", "Admin"];

//   const [searchName, setSearchName] = useState("");
//   const [searchDate, setSearchDate] = useState("");
//   const [filterType, setFilterType] = useState("");

//   const canAddHoliday = user?.roles?.some((role) =>
//     ALLOWED_ROLES_FOR_ADD.includes(role),
//   );

//   // Form state
//   const [formData, setFormData] = useState({
//     holidayName: "",
//     holidayDate: "",
//     yearId: "",
//     monthId: "",
//     holidayType: "",
//   });

//   // Delete confirmation modal state
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [holidayToDelete, setHolidayToDelete] = useState(null);
//   const [deleteType, setDeleteType] = useState(null); // "soft" or "hard"

//   // Load Holidays based on view mode
//   const loadHolidays = async () => {
//     try {
//       let data;
//       if (viewMode === "active") {
//         data = await getHolidaysByOrgId(user?.organisationId);
//       } else if (viewMode === "deleted") {
//         data = await getDeletedHolidaysByOrgId(user?.organisationId);
//       } else {
//         data = await getAllHolidaysByOrgId(user?.organisationId);
//       }
//       setHolidays(data || []);
//     } catch (error) {
//       toast.error("Error fetching holidays");
//     }
//   };

//   const MONTH_MAP = {
//     "01": "January",
//     "02": "February",
//     "03": "March",
//     "04": "April",
//     "05": "May",
//     "06": "June",
//     "07": "July",
//     "08": "August",
//     "09": "September",
//     10: "October",
//     11: "November",
//     12: "December",
//   };

//   const HOLIDAY_TYPE_OPTIONS = [
//     { value: "", label: "Select Holiday Type" },
//     { value: "National", label: "National" },
//     { value: "Festival", label: "Festival" },
//     { value: "Optional", label: "Optional" },
//     { value: "Company", label: "Company" },
//     { value: "Weekend", label: "Weekend" },
//     { value: "Restricted", label: "Restricted" },
//   ];

//   useEffect(() => {
//     if (user?.organisationId) {
//       loadHolidays();
//     }
//   }, [user?.organisationId, viewMode]);

//   // Save Holiday (Add/Update)
//   const handleSaveHoliday = async () => {
//     if (!formData.holidayName || !formData.holidayType) {
//       toast.error("Holiday name and type are required");
//       return;
//     }

//     try {
//       setLoading(true);

//       const payload = {
//         ...formData,
//         orgId: String(user.organisationId),
//         holidayDate: formData.holidayDate || null,
//         ...(isEditMode && { holidayId: editingHolidayId }),
//       };

//       if (isEditMode) {
//         await updateHoliday(payload, String(user?.userId));
//         toast.success("Holiday updated successfully");
//       } else {
//         await createHoliday(payload, String(user?.userId));
//         toast.success("Holiday added successfully");
//       }

//       setShowModal(false);
//       setIsEditMode(false);
//       setEditingHolidayId(null);

//       setFormData({
//         holidayName: "",
//         holidayDate: "",
//         yearId: "",
//         monthId: "",
//         holidayType: "",
//       });

//       loadHolidays();
//     } catch (error) {
//       toast.error("Error saving holiday");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Handle Soft Delete
//   const handleSoftDelete = async () => {
//     if (!holidayToDelete) return;

//     try {
//       await softDeleteHoliday(holidayToDelete.holidayId, String(user?.userId));
//       toast.success("Holiday deleted successfully");
//       setShowDeleteModal(false);
//       setHolidayToDelete(null);
//       loadHolidays();
//     } catch (error) {
//       toast.error("Error deleting holiday");
//     }
//   };

//   // Handle Hard Delete (Permanent)
//   const handleHardDelete = async () => {
//     if (!holidayToDelete) return;

//     try {
//       await hardDeleteHoliday(holidayToDelete.holidayId);
//       toast.success("Holiday permanently deleted");
//       setShowDeleteModal(false);
//       setHolidayToDelete(null);
//       loadHolidays();
//     } catch (error) {
//       toast.error("Error permanently deleting holiday");
//     }
//   };

//   // Handle Restore
//   const handleRestore = async (holiday) => {
//     if (
//       window.confirm(
//         `Are you sure you want to restore "${holiday.holidayName}"?`,
//       )
//     ) {
//       try {
//         await restoreHoliday(holiday.holidayId, String(user?.userId));
//         toast.success("Holiday restored successfully");
//         loadHolidays();
//       } catch (error) {
//         toast.error("Error restoring holiday");
//       }
//     }
//   };

//   const handleDateChange = (dateValue) => {
//     if (!dateValue) {
//       setFormData({
//         ...formData,
//         holidayDate: "",
//         monthId: "",
//         yearId: "",
//       });
//       return;
//     }

//     const selectedDate = new Date(dateValue);
//     const monthNumber = String(selectedDate.getMonth() + 1).padStart(2, "0");
//     const year = selectedDate.getFullYear();

//     setFormData({
//       ...formData,
//       holidayDate: dateValue,
//       monthId: monthNumber,
//       yearId: String(year),
//     });
//   };

//   const handleEditHoliday = (holiday) => {
//     setIsEditMode(true);
//     setEditingHolidayId(holiday.holidayId);

//     setFormData({
//       holidayName: holiday.holidayName,
//       holidayDate: holiday.holidayDate
//         ? moment(holiday.holidayDate).format("YYYY-MM-DD")
//         : "",
//       monthId: holiday.monthId || "",
//       yearId: holiday.yearId || "",
//       holidayType: holiday.holidayType,
//     });

//     setShowModal(true);
//   };

//   const filteredHolidays = holidays.filter((holiday) => {
//     const matchesName = holiday.holidayName
//       ?.toLowerCase()
//       .includes(searchName.toLowerCase());

//     const matchesDate = searchDate
//       ? moment(holiday.holidayDate).format("YYYY-MM-DD") === searchDate
//       : true;

//     const matchesType = filterType ? holiday.holidayType === filterType : true;

//     return matchesName && matchesDate && matchesType;
//   });

//   return (
//     <div className="main-content expand">
//       <Container fluid className="list-container">
//         {/* Header */}
//         <div className="header-background">
//           <div className="heading-name">
//             <h1 className="header-list-title">Holiday Master</h1>
//           </div>

//           <div className="d-flex gap-2">
//             {/* View Mode Toggle */}
//             <div className="btn-group">
//               <Button
//                 variant={viewMode === "active" ? "primary" : "outline-primary"}
//                 onClick={() => setViewMode("active")}
//               >
//                 Active
//               </Button>
//               <Button
//                 variant={viewMode === "deleted" ? "danger" : "outline-danger"}
//                 onClick={() => setViewMode("deleted")}
//               >
//                 Deleted
//               </Button>
//               <Button
//                 variant={viewMode === "all" ? "secondary" : "outline-secondary"}
//                 onClick={() => setViewMode("all")}
//               >
//                 All
//               </Button>
//             </div>

//             {canAddHoliday && viewMode !== "deleted" && (
//               <Button
//                 className="gradient-btn green-btn"
//                 onClick={() => setShowModal(true)}
//               >
//                 <FaPlus style={{ marginRight: 8 }} />
//                 Add Holiday
//               </Button>
//             )}
//           </div>
//         </div>

//         {/* Info Alert for Deleted View */}
//         {viewMode === "deleted" && (
//           <Alert variant="info" className="mt-3">
//             <FaEye className="me-2" />
//             Showing soft-deleted holidays. You can restore them or permanently
//             delete them.
//           </Alert>
//         )}

//         {/* Filter Section */}
//         <Row className="mb-3 mt-3">
//           <Col md={4}>
//             <Form.Control
//               type="text"
//               placeholder="Search by Holiday Name"
//               value={searchName}
//               onChange={(e) => setSearchName(e.target.value)}
//             />
//           </Col>

//           <Col md={3}>
//             <Form.Control
//               type="date"
//               value={searchDate}
//               onChange={(e) => setSearchDate(e.target.value)}
//             />
//           </Col>

//           <Col md={3}>
//             <Form.Select
//               value={filterType}
//               onChange={(e) => setFilterType(e.target.value)}
//             >
//               <option value="">All Types</option>
//               {HOLIDAY_TYPE_OPTIONS.slice(1).map((type) => (
//                 <option key={type.value} value={type.value}>
//                   {type.label}
//                 </option>
//               ))}
//             </Form.Select>
//           </Col>

//           <Col md={2}>
//             <Button
//               variant="outline-danger"
//               onClick={() => {
//                 setSearchName("");
//                 setSearchDate("");
//                 setFilterType("");
//               }}
//             >
//               RESET
//             </Button>
//           </Col>
//         </Row>

//         {/* Table */}
//         <div
//           className="table-wrapper"
//           style={{
//             maxHeight: "80vh",
//             overflow: "scroll",
//           }}
//         >
//           <Table bordered hover className="data-table">
//             <thead>
//               <tr>
//                 <th>#</th>
//                 <th>Holiday Name</th>
//                 <th>Date</th>
//                 <th>Type</th>
//                 {viewMode === "all" && <th>Status</th>}
//                 <th>Actions</th>
//               </tr>
//             </thead>

//             <tbody>
//               {filteredHolidays.length > 0 ? (
//                 filteredHolidays.map((holiday, index) => (
//                   <tr
//                     key={holiday.holidayId}
//                     className={!holiday.isActive ? "table-danger" : ""}
//                   >
//                     <td>{index + 1}</td>
//                     <td>{holiday.holidayName}</td>
//                     <td>
//                       {holiday.holidayDate
//                         ? moment(holiday.holidayDate).format("DD-MMM-YYYY")
//                         : "-"}
//                     </td>
//                     <td>{holiday.holidayType}</td>
//                     {viewMode === "all" && (
//                       <td>
//                         <span
//                           className={
//                             holiday.isActive
//                               ? "badge bg-success"
//                               : "badge bg-danger"
//                           }
//                         >
//                           {holiday.isActive ? "Active" : "Deleted"}
//                         </span>
//                       </td>
//                     )}
//                     <td
//                       style={{
//                         display: "flex",
//                         justifyContent: "center",
//                         gap: "5px",
//                       }}
//                     >
//                       {viewMode === "deleted" ? (
//                         <>
//                           <Button
//                             size="sm"
//                             variant="success"
//                             onClick={() => handleRestore(holiday)}
//                           >
//                             <FaTrashRestore /> Restore
//                           </Button>
//                           <Button
//                             size="sm"
//                             variant="danger"
//                             onClick={() => {
//                               setHolidayToDelete(holiday);
//                               setDeleteType("hard");
//                               setShowDeleteModal(true);
//                             }}
//                           >
//                             <FaTrash /> Permanent Delete
//                           </Button>
//                         </>
//                       ) : (
//                         <>
//                           {canAddHoliday && (
//                             <Button
//                               size="sm"
//                               variant="warning"
//                               onClick={() => handleEditHoliday(holiday)}
//                             >
//                               Update
//                             </Button>
//                           )}
//                           {canAddHoliday && (
//                             <Button
//                               size="sm"
//                               variant="danger"
//                               onClick={() => {
//                                 setHolidayToDelete(holiday);
//                                 setDeleteType("soft");
//                                 setShowDeleteModal(true);
//                               }}
//                             >
//                               <FaTrash /> Delete
//                             </Button>
//                           )}
//                         </>
//                       )}
//                     </td>
//                   </tr>
//                 ))
//               ) : (
//                 <tr>
//                   <td
//                     colSpan={viewMode === "all" ? 6 : 5}
//                     style={{ textAlign: "center", fontWeight: 600 }}
//                   >
//                     No holidays found
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </Table>
//         </div>

//         {/* Delete Confirmation Modal */}
//         <Modal
//           show={showDeleteModal}
//           onHide={() => setShowDeleteModal(false)}
//           centered
//         >
//           <Modal.Header closeButton>
//             <Modal.Title>
//               {deleteType === "soft"
//                 ? "Delete Holiday"
//                 : "Permanently Delete Holiday"}
//             </Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             {deleteType === "soft" ? (
//               <>
//                 <p>
//                   Are you sure you want to delete{" "}
//                   <strong>{holidayToDelete?.holidayName}</strong>?
//                 </p>
//                 <p className="text-muted">
//                   This holiday will be moved to trash. You can restore it later.
//                 </p>
//               </>
//             ) : (
//               <>
//                 <p>
//                   Are you sure you want to{" "}
//                   <strong className="text-danger">PERMANENTLY DELETE</strong>{" "}
//                   <strong>{holidayToDelete?.holidayName}</strong>?
//                 </p>
//                 <p className="text-danger">This action cannot be undone!</p>
//               </>
//             )}
//           </Modal.Body>
//           <Modal.Footer>
//             <Button
//               variant="secondary"
//               onClick={() => setShowDeleteModal(false)}
//             >
//               Cancel
//             </Button>
//             <Button
//               variant={deleteType === "soft" ? "danger" : "dark"}
//               onClick={
//                 deleteType === "soft" ? handleSoftDelete : handleHardDelete
//               }
//             >
//               {deleteType === "soft" ? "Delete" : "Permanently Delete"}
//             </Button>
//           </Modal.Footer>
//         </Modal>

//         {/* Add/Edit Holiday Modal */}
//         <Modal
//           show={showModal}
//           onHide={() => {
//             setShowModal(false);
//             setIsEditMode(false);
//             setEditingHolidayId(null);
//           }}
//           centered
//         >
//           <Modal.Header closeButton>
//             <Modal.Title>
//               {isEditMode ? "Update Holiday" : "Add Holiday"}
//             </Modal.Title>
//           </Modal.Header>

//           <Modal.Body>
//             <Form>
//               <Row>
//                 <Col md={12}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Holiday Name</Form.Label>
//                     <Form.Control
//                       value={formData.holidayName}
//                       onChange={(e) =>
//                         setFormData({
//                           ...formData,
//                           holidayName: e.target.value,
//                         })
//                       }
//                     />
//                   </Form.Group>
//                 </Col>

//                 <Col md={6}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Holiday Date</Form.Label>
//                     <Form.Control
//                       type="date"
//                       value={formData.holidayDate}
//                       onChange={(e) => handleDateChange(e.target.value)}
//                     />
//                   </Form.Group>
//                 </Col>

//                 <Col md={6}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Holiday Type</Form.Label>
//                     <Form.Select
//                       value={formData.holidayType}
//                       onChange={(e) =>
//                         setFormData({
//                           ...formData,
//                           holidayType: e.target.value,
//                         })
//                       }
//                     >
//                       {HOLIDAY_TYPE_OPTIONS.map((type) => (
//                         <option key={type.value} value={type.value}>
//                           {type.label}
//                         </option>
//                       ))}
//                     </Form.Select>
//                   </Form.Group>
//                 </Col>

//                 <Col md={6}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Month</Form.Label>
//                     <Form.Control
//                       value={MONTH_MAP[formData.monthId] || ""}
//                       disabled
//                     />
//                   </Form.Group>
//                 </Col>

//                 <Col md={6}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Year</Form.Label>
//                     <Form.Control
//                       value={formData.yearId}
//                       onChange={(e) =>
//                         setFormData({ ...formData, yearId: e.target.value })
//                       }
//                       disabled
//                     />
//                   </Form.Group>
//                 </Col>
//               </Row>
//             </Form>
//           </Modal.Body>

//           <Modal.Footer>
//             <Button variant="secondary" onClick={() => setShowModal(false)}>
//               Cancel
//             </Button>
//             <Button onClick={handleSaveHoliday} disabled={loading}>
//               {loading
//                 ? isEditMode
//                   ? "Updating..."
//                   : "Saving..."
//                 : isEditMode
//                   ? "Update"
//                   : "Save"}
//             </Button>
//           </Modal.Footer>
//         </Modal>
//       </Container>
//     </div>
//   );
// };

// export default HolidayMasterOrg;

// import { useEffect, useState } from "react";
// import {
//   Container,
//   Table,
//   Button,
//   Modal,
//   Form,
//   Row,
//   Col,
// } from "react-bootstrap";
// import { FaPlus } from "react-icons/fa";
// import { toast } from "react-toastify";
// import moment from "moment";
// import { useAuth } from "../Context/AuthContext";
// import "../Styling/AllTables.css";

// import {
//   getHolidaysByOrgId,
//   createHoliday,
//   updateHoliday,
// } from "../Api/HolidayMasterApi";

// const HolidayMasterOrg = () => {
//   const { user } = useAuth();
//   console.log("Logged-in user: ", user);
//   const [isEditMode, setIsEditMode] = useState(false);
//   const [editingHolidayId, setEditingHolidayId] = useState(null);

//   const [holidays, setHolidays] = useState([]);
//   const [showModal, setShowModal] = useState(false);
//   const [loading, setLoading] = useState(false);
//   const ALLOWED_ROLES_FOR_ADD = ["HR", "Admin"];

//   const [searchName, setSearchName] = useState("");
//   const [searchDate, setSearchDate] = useState("");
//   const [filterType, setFilterType] = useState("");

//   const canAddHoliday = user?.roles?.some((role) =>
//     ALLOWED_ROLES_FOR_ADD.includes(role),
//   );

//   // 🔹 Form state
//   const [formData, setFormData] = useState({
//     holidayName: "",
//     holidayDate: "",
//     yearId: "",
//     monthId: "",
//     holidayType: "",
//   });

//   // =========================
//   // Load Holidays
//   // =========================
//   const loadHolidays = async () => {
//     try {
//       const data = await getHolidaysByOrgId(user?.organisationId);
//       setHolidays(data || []);
//     } catch (error) {
//       toast.error("Error fetching holidays");
//     }
//   };

//   const MONTH_MAP = {
//     "01": "January",
//     "02": "February",
//     "03": "March",
//     "04": "April",
//     "05": "May",
//     "06": "June",
//     "07": "July",
//     "08": "August",
//     "09": "September",
//     10: "October",
//     11: "November",
//     12: "December",
//   };

//   const HOLIDAY_TYPE_OPTIONS = [
//     { value: "", label: "Select Holiday Type" },
//     { value: "National", label: "National" },
//     { value: "Festival", label: "Festival" },
//     { value: "Optional", label: "Optional" },
//     { value: "Company", label: "Company" },
//     { value: "Weekend", label: "Weekend" },
//     { value: "Restricted", label: "Restricted" },
//   ];

//   useEffect(() => {
//     if (user?.organisationId) {
//       loadHolidays();
//     }
//   }, [user?.organisationId]);

//   // =========================
//   // Add Holiday
//   // =========================
//   const handleSaveHoliday = async () => {
//     if (!formData.holidayName || !formData.holidayType) {
//       toast.error("Holiday name and type are required");
//       return;
//     }

//     try {
//       setLoading(true);

//       const payload = {
//         ...formData,
//         orgId: String(user.organisationId),
//         holidayDate: formData.holidayDate || null,
//         ...(isEditMode && { holidayId: editingHolidayId }),
//       };

//       if (isEditMode) {
//         await updateHoliday(payload, String(user?.userId));
//         toast.success("Holiday updated successfully");
//       } else {
//         await createHoliday(payload, String(user?.userId));
//         toast.success("Holiday added successfully");
//       }

//       setShowModal(false);
//       setIsEditMode(false);
//       setEditingHolidayId(null);

//       setFormData({
//         holidayName: "",
//         holidayDate: "",
//         yearId: "",
//         monthId: "",
//         holidayType: "",
//       });

//       loadHolidays();
//     } catch (error) {
//       toast.error("Error saving holiday");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDateChange = (dateValue) => {
//     if (!dateValue) {
//       setFormData({
//         ...formData,
//         holidayDate: "",
//         monthId: "",
//         yearId: "",
//       });
//       return;
//     }

//     const selectedDate = new Date(dateValue);

//     const monthNumber = String(selectedDate.getMonth() + 1).padStart(2, "0");
//     const year = selectedDate.getFullYear();

//     setFormData({
//       ...formData,
//       holidayDate: dateValue,
//       monthId: monthNumber, // ✅ STORE 01,02...
//       yearId: String(year),
//     });
//   };

//   const handleEditHoliday = (holiday) => {
//     setIsEditMode(true);
//     setEditingHolidayId(holiday.holidayId);

//     setFormData({
//       holidayName: holiday.holidayName,
//       holidayDate: holiday.holidayDate
//         ? moment(holiday.holidayDate).format("YYYY-MM-DD")
//         : "",
//       monthId: holiday.monthId || "",
//       yearId: holiday.yearId || "",
//       holidayType: holiday.holidayType,
//     });

//     setShowModal(true);
//   };

//   const filteredHolidays = holidays.filter((holiday) => {
//     const matchesName = holiday.holidayName
//       ?.toLowerCase()
//       .includes(searchName.toLowerCase());

//     const matchesDate = searchDate
//       ? moment(holiday.holidayDate).format("YYYY-MM-DD") === searchDate
//       : true;

//     const matchesType = filterType ? holiday.holidayType === filterType : true;

//     return matchesName && matchesDate && matchesType;
//   });

//   return (
//     <div className="main-content expand">
//       <Container fluid className="list-container">
//         {/* ================= HEADER ================= */}
//         <div className="header-background">
//           <div className="heading-name">
//             <h1 className="header-list-title">Holiday Master</h1>
//           </div>

//           {canAddHoliday && (
//             <Button
//               className="gradient-btn green-btn"
//               onClick={() => setShowModal(true)}
//             >
//               <FaPlus style={{ marginRight: 8 }} />
//               Add Holiday
//             </Button>
//           )}
//         </div>

//         {/* ================= FILTER SECTION ================= */}
//         <Row className="mb-3">
//           <Col md={4}>
//             <Form.Control
//               type="text"
//               placeholder="Search by Holiday Name"
//               value={searchName}
//               onChange={(e) => setSearchName(e.target.value)}
//             />
//           </Col>

//           <Col md={3}>
//             <Form.Control
//               type="date"
//               value={searchDate}
//               onChange={(e) => setSearchDate(e.target.value)}
//             />
//           </Col>

//           <Col md={3}>
//             <Form.Select
//               value={filterType}
//               onChange={(e) => setFilterType(e.target.value)}
//             >
//               <option value="">All Types</option>
//               {HOLIDAY_TYPE_OPTIONS.slice(1).map((type) => (
//                 <option key={type.value} value={type.value}>
//                   {type.label}
//                 </option>
//               ))}
//             </Form.Select>
//           </Col>

//           <Col md={2}>
//             <Button
//               variant="outline-danger"
//               onClick={() => {
//                 setSearchName("");
//                 setSearchDate("");
//                 setFilterType("");
//               }}
//             >
//               RESET
//             </Button>
//           </Col>
//         </Row>

//         {/* ================= TABLE ================= */}
//         <div
//           className="table-wrapper"
//           style={{
//             maxHeight: "80vh",
//             overflow: "scroll",
//           }}
//         >
//           <Table bordered hover className="data-table">
//             <thead>
//               <tr>
//                 <th>#</th>
//                 {/* <th>Holiday ID</th> */}
//                 <th>Holiday Name</th>
//                 <th>Date</th>
//                 {/* <th>Month</th> */}
//                 {/* <th>Year</th> */}
//                 <th>Type</th>
//                 <th>Actions</th>
//               </tr>
//             </thead>

//             <tbody>
//               {filteredHolidays.length > 0 ? (
//                 filteredHolidays.map((holiday, index) => (
//                   <tr key={holiday.holidayId}>
//                     <td>{index + 1}</td>
//                     {/* <td>{holiday.holidayId}</td> */}
//                     <td>{holiday.holidayName}</td>
//                     <td>
//                       {holiday.holidayDate
//                         ? moment(holiday.holidayDate).format("DD-MMM-YYYY")
//                         : "-"}
//                     </td>
//                     {/* <td>{MONTH_MAP[holiday.monthId] || "-"}</td> */}
//                     {/* <td>{holiday.yearId}</td> */}
//                     <td>{holiday.holidayType}</td>
//                     <td style={{ display: "flex", justifyContent: "center" }}>
//                       {canAddHoliday && (
//                         <Button
//                           size="sm"
//                           variant="warning"
//                           onClick={() => handleEditHoliday(holiday)}
//                         >
//                           Update
//                         </Button>
//                       )}
//                     </td>
//                   </tr>
//                 ))
//               ) : (
//                 <tr>
//                   <td
//                     colSpan={7}
//                     style={{ textAlign: "center", fontWeight: 600 }}
//                   >
//                     No holidays found
//                   </td>
//                 </tr>
//               )}
//             </tbody>
//           </Table>
//         </div>

//         {/* ================= ADD HOLIDAY MODAL ================= */}
//         <Modal
//           show={showModal}
//           onHide={() => {
//             setShowModal(false);
//             setIsEditMode(false);
//             setEditingHolidayId(null);
//           }}
//           centered
//         >
//           <Modal.Header closeButton>
//             <Modal.Title>
//               {isEditMode ? "Update Holiday" : "Add Holiday"}
//             </Modal.Title>
//           </Modal.Header>

//           <Modal.Body>
//             <Form>
//               <Row>
//                 <Col md={12}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Holiday Name</Form.Label>
//                     <Form.Control
//                       value={formData.holidayName}
//                       onChange={(e) =>
//                         setFormData({
//                           ...formData,
//                           holidayName: e.target.value,
//                         })
//                       }
//                     />
//                   </Form.Group>
//                 </Col>

//                 <Col md={6}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Holiday Date</Form.Label>
//                     <Form.Control
//                       type="date"
//                       value={formData.holidayDate}
//                       onChange={(e) => handleDateChange(e.target.value)}
//                     />
//                   </Form.Group>
//                 </Col>

//                 <Col md={6}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Holiday Type</Form.Label>
//                     <Form.Select
//                       value={formData.holidayType}
//                       onChange={(e) =>
//                         setFormData({
//                           ...formData,
//                           holidayType: e.target.value,
//                         })
//                       }
//                     >
//                       {HOLIDAY_TYPE_OPTIONS.map((type) => (
//                         <option key={type.value} value={type.value}>
//                           {type.label}
//                         </option>
//                       ))}
//                     </Form.Select>
//                   </Form.Group>
//                 </Col>

//                 <Col md={6}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Month</Form.Label>
//                     <Form.Control
//                       value={MONTH_MAP[formData.monthId] || ""}
//                       disabled
//                     />
//                   </Form.Group>
//                 </Col>

//                 <Col md={6}>
//                   <Form.Group className="mb-2">
//                     <Form.Label>Year</Form.Label>
//                     <Form.Control
//                       value={formData.yearId}
//                       onChange={(e) =>
//                         setFormData({ ...formData, yearId: e.target.value })
//                       }
//                       disabled
//                     />
//                   </Form.Group>
//                 </Col>
//               </Row>
//             </Form>
//           </Modal.Body>

//           <Modal.Footer>
//             <Button variant="secondary" onClick={() => setShowModal(false)}>
//               Cancel
//             </Button>
//             <Button onClick={handleSaveHoliday} disabled={loading}>
//               {loading
//                 ? isEditMode
//                   ? "Updating..."
//                   : "Saving..."
//                 : isEditMode
//                   ? "Update"
//                   : "Save"}
//             </Button>
//           </Modal.Footer>
//         </Modal>
//       </Container>
//     </div>
//   );
// };

// export default HolidayMasterOrg;
